import { Link } from "@tanstack/react-router";
import { cn } from "@dl/web-core-js/ui";
import type { ReactNode } from "react";

/**
 * A SKU that navigates to its product.
 *
 * Products are referenced from most screens in the app — the pipeline, transfer
 * plans, projects, bundle items, variations, every pricing grid — and until now
 * the SKU was inert text on all of them, so getting to the product meant going
 * to Products and searching for a code you were already looking at.
 *
 * Rendered as plain text when there is no id: several of these screens join
 * on SKU and can legitimately fail to match, and a link that goes nowhere is
 * worse than no link.
 *
 * Inside a row that is itself clickable, the click is stopped here so the link
 * wins over the row's own handler rather than racing it.
 */
export function ProductLink({
  id,
  children,
  className,
}: {
  id: number | null | undefined;
  /** Usually the SKU. Anything renderable works. */
  children: ReactNode;
  className?: string;
}) {
  if (id === null || id === undefined) {
    return <span className={className}>{children}</span>;
  }

  return (
    <Link
      to="/products/$productId"
      params={{ productId: String(id) }}
      onClick={(event) => event.stopPropagation()}
      className={cn("rounded-sm hover:text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent", className)}
    >
      {children}
    </Link>
  );
}
